import { CraneSideEnum, VesselPartTypeEnum } from "open-vessel-definition";
import { IFinding, IValidationInput, IValidationOutput, IValidator } from "../types";
import {
  isFiniteNumber,
  isForeAft,
  isIsoBayKey,
  isPlainObject,
  parseIsoBayRange,
} from "./patterns";

import { createValidationOutput } from "../helpers/createValidationOutput";

const name = "vesselPartsStructureValidator";
const okTranslationKey = "validators:structure.vesselPartsStructure.ok";
const errorTranslationKey = "validators:structure.vesselPartsStructure.error";

export type TVesselPartsStructureCode =
  | "missing"
  | "wrongType"
  | "badEnum"
  | "outOfBounds"
  | "duplicate"
  /** `slotRefId` is not a Bay of the vessel, a `"A-B"` Bay range, or another part's `id` */
  | "unresolvedRef"
  /** Following `slotRefId` from part to part comes back to the start */
  | "refCycle";

export interface IVesselPartsStructureFinding extends IFinding {
  code: TVesselPartsStructureCode;
  path: string;
  index: number;
  id?: string;
}

export interface IVesselPartsStructureOutput {
  findings: IVesselPartsStructureFinding[];
}

const PART_TYPES: readonly string[] = [
  VesselPartTypeEnum.SPACER,
  VesselPartTypeEnum.BRIDGE,
  VesselPartTypeEnum.SMOKE,
  VesselPartTypeEnum.CRANE,
];

const CRANE_SIDES: readonly string[] = [
  CraneSideEnum.PORT,
  CraneSideEnum.STARBOARD,
  CraneSideEnum.CENTER,
];

/**
 * The shape of every vessel part: its type and the fields that type requires, unique
 * ids, and a `slotRefId` that resolves to a Bay of the vessel, a Bay range or another
 * part, without cycles. A part that cannot be placed is silently dropped by the
 * drawing code, which is why this is an error. Never throws.
 */
export const vesselPartsStructureValidator: IValidator<IVesselPartsStructureOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: vesselPartsStructureValidatorFn,
  severity: "error",
  stage: "structure",
};

export function vesselPartsStructureValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<IVesselPartsStructureOutput> {
  const findings: IVesselPartsStructureFinding[] = [];
  const parts: unknown[] = Array.isArray(json?.vesselPartsData) ? json.vesselPartsData : [];
  const baysData: unknown[] = Array.isArray(json?.baysData) ? json.baysData : [];

  const isoBays = new Set<string>();
  baysData.forEach((bl) => {
    if (isPlainObject(bl) && typeof bl["isoBay"] === "string") isoBays.add(bl["isoBay"]);
  });

  const ids = new Map<string, number>();
  parts.forEach((part, index) => {
    if (isPlainObject(part) && typeof part["id"] === "string") {
      if (ids.has(part["id"]))
        findings.push({ code: "duplicate", path: "id", index, id: part["id"] });
      else ids.set(part["id"], index);
    }
  });

  parts.forEach((part, index) => {
    if (!isPlainObject(part)) {
      findings.push({ code: "wrongType", path: "", index });
      return;
    }

    const id = typeof part["id"] === "string" ? part["id"] : undefined;
    const flag = (code: TVesselPartsStructureCode, path: string) =>
      findings.push({ code, path, index, id });

    (["id", "label", "slotRefId"] as const).forEach((f) => {
      if (part[f] === undefined) flag("missing", f);
      else if (typeof part[f] !== "string") flag("wrongType", f);
    });

    if (part["posRef"] === undefined) flag("missing", "posRef");
    else if (!isForeAft(part["posRef"])) flag("badEnum", "posRef");

    if (part["len"] === undefined) flag("missing", "len");
    else if (!isFiniteNumber(part["len"])) flag("wrongType", "len");
    else if (part["len"] <= 0) flag("outOfBounds", "len");

    const type = part["type"];
    if (type === undefined) flag("missing", "type");
    else if (!PART_TYPES.includes(type as string)) flag("badEnum", "type");
    else checkTypeFields(part, type as VesselPartTypeEnum, flag);

    checkRef(part, ids, isoBays, parts, flag);
  });

  return createValidationOutput(vesselPartsStructureValidator, findings, { findings });
}

type TFlag = (code: TVesselPartsStructureCode, path: string) => void;

function checkTypeFields(
  part: Record<string, unknown>,
  type: VesselPartTypeEnum,
  flag: TFlag,
): void {
  switch (type) {
    case VesselPartTypeEnum.CRANE: {
      const cranes = part["cranes"];
      if (cranes === undefined) return flag("missing", "cranes");
      if (!Array.isArray(cranes)) return flag("wrongType", "cranes");
      if (cranes.length === 0) flag("outOfBounds", "cranes");
      cranes.forEach((c, i) => {
        if (!CRANE_SIDES.includes(c as string)) flag("badEnum", `cranes.${i}`);
      });
      return;
    }
    case VesselPartTypeEnum.SMOKE: {
      const n = part["numberOfSmokeStacks"];
      if (n === undefined) return flag("missing", "numberOfSmokeStacks");
      if (!isFiniteNumber(n)) return flag("wrongType", "numberOfSmokeStacks");
      if (n < 1 || !Number.isInteger(n)) flag("outOfBounds", "numberOfSmokeStacks");
      return;
    }
    case VesselPartTypeEnum.BRIDGE:
    case VesselPartTypeEnum.SPACER:
      if (part["heatSrcBelow"] === undefined) flag("missing", "heatSrcBelow");
      else if (typeof part["heatSrcBelow"] !== "boolean") flag("wrongType", "heatSrcBelow");
      return;
    default:
      return;
  }
}

function checkRef(
  part: Record<string, unknown>,
  ids: Map<string, number>,
  isoBays: Set<string>,
  parts: unknown[],
  flag: TFlag,
): void {
  const ref = part["slotRefId"];
  if (typeof ref !== "string") return;

  if (isIsoBayKey(ref)) {
    if (!isoBays.has(ref)) flag("unresolvedRef", "slotRefId");
    return;
  }

  const range = parseIsoBayRange(ref);
  if (range !== undefined) {
    if (!isoBays.has(range[0]) || !isoBays.has(range[1])) flag("unresolvedRef", "slotRefId");
    return;
  }

  if (!ids.has(ref)) return flag("unresolvedRef", "slotRefId");

  // Follow the chain of part references; it must end in a Bay
  const visited = new Set<string>();
  let current: string | undefined = ref;
  if (typeof part["id"] === "string") visited.add(part["id"]);

  while (current !== undefined && ids.has(current)) {
    if (visited.has(current)) return flag("refCycle", "slotRefId");
    visited.add(current);
    const next: unknown = parts[ids.get(current) as number];
    current =
      isPlainObject(next) && typeof next["slotRefId"] === "string" ? next["slotRefId"] : undefined;
  }
}
